/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * LocationSearchField
 * -------------------
 * An address and the coordinates that go with it, as one control.
 *
 * The columns behind it are a text address and a geo_point, and nobody knows a
 * start line's latitude. So the address box is the real input: type a place,
 * pick a match, and both coordinates are filled in from it.
 *
 * Three deliberate choices:
 *
 *  • **Debounced at 500ms, aborted on change.** Nominatim is a free public
 *    service that asks callers to stay near one request a second. A lookup per
 *    keystroke would be rude and would rate-limit the organizer out mid-form.
 *
 *  • **Failure is quiet.** `geocode` returns [] for every failure — offline,
 *    rate-limited, genuinely no match — and this says "No matches" and opens
 *    the manual fields. Search is a convenience; the form must never become
 *    unusable because a third-party service is down.
 *
 *  • **Manual entry is always reachable**, and opens by itself when there is an
 *    address but no coordinates — the exact state an unnamed place leaves you
 *    in, and the one where the organizer most needs to be shown the way out.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import OrgField from './OrgField';
import OrgInput from './OrgInput';
import { geocode } from '../../athlohive/geocode';
import {
  coordinateError, isValidPoint, sanitizeCoordinate,
} from '../../athlohive/coordinates';
import MapPointPicker from './MapPointPicker';

/** Long enough that a pause is deliberate, short enough not to feel laggy. */
const DEBOUNCE_MS = 500;

/** Below this a query matches half the planet and the results are useless. */
const MIN_QUERY_LENGTH = 3;

export default function LocationSearchField({
  label, address, point, onChange, required, error, help,
  latLabel = 'Latitude', lngLabel = 'Longitude',
  // A map under the box. Off by default so the event's city field — which has
  // no geo_point column to write to — does not grow one.
  showMap = false,
  // A locked event still shows its map; it just cannot be moved. The text
  // inputs are disabled by the fieldset above, but the map is a div with click
  // handlers, which a fieldset cannot reach.
  readOnly = false,
  // Multi-line address box. A start line's address runs to a couple of lines
  // more often than not, and the columns behind these are `text`.
  multiline = false,
  // Some places have coordinates to store and some do not. `event` keeps only a
  // text location plus state/country/pincode — no geo_point at all — so offering
  // a lat/lng control there would collect numbers with nowhere to put them, and
  // then quietly drop them. Off means: search only.
  showCoordinates = true,
}) {
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  // An address with no coordinates is the state manual entry exists for, so
  // start open rather than making them find the link.
  const [manualOpen, setManualOpen] = useState(
    !!address && (!point?.lat || !point?.lng)
  );
  const abortRef = useRef(null);
  // Whether the address in the box got there by the organizer TYPING it.
  //
  // The search must answer to typing, not to the `address` prop changing. Those
  // look identical from inside an effect keyed on `address`, and the difference
  // matters: this component is remounted every time the wizard switches step,
  // because the shell renders a different step component per index. On each
  // remount the effect saw a non-empty address it had never searched for and
  // looked it up again — a Nominatim request nobody asked for, and the
  // suggestion list springing open over the fields it had already filled.
  //
  // A ref cannot remember across that remount, so remembering the last resolved
  // value could not fix it. Asking "did a keystroke cause this?" can: false on
  // mount, false after a choice, true only when the input's own handler ran.
  const typedRef = useRef(false);

  const emit = useCallback((next) => {
    onChange({
      address: next.address ?? address ?? '',
      lat: next.lat ?? point?.lat ?? '',
      lng: next.lng ?? point?.lng ?? '',
    });
  }, [onChange, address, point]);

  // Typing only changes the address. The coordinates stay whatever they were
  // until a suggestion is chosen or they are typed — silently blanking them
  // because a character changed would lose work.
  const onAddressChange = (value) => {
    typedRef.current = true;
    emit({ address: value });
  };

  useEffect(() => {
    const query = (address || '').trim();

    // Nobody typed this: it arrived as a prop, either on the first render or on
    // a remount after a step switch. There is nothing to look up.
    if (!typedRef.current) return undefined;

    if (query.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setSearched(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setSearching(true);
      const found = await geocode(query, { signal: controller.signal });
      if (controller.signal.aborted) return;
      setResults(found);
      setSearched(true);
      setSearching(false);
      // Nothing came back and there is still nowhere to put a coordinate: show
      // the way out rather than leaving a dead end.
      if (!found.length && (!point?.lat || !point?.lng)) setManualOpen(true);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // `point` is deliberately absent: choosing a suggestion sets it, and
    // re-running the search because of that would query for the address we
    // just resolved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const choose = (match) => {
    // The label is now the field's value, but the organizer did not type it, so
    // it must not start a search.
    typedRef.current = false;
    // The administrative parts travel with the choice. A caller that has fields
    // for them (the event's state / country / pincode) fills them in; one that
    // does not simply ignores the extra keys.
    onChange({
      address: match.label,
      lat: match.lat,
      lng: match.lng,
      state: match.state || '',
      country: match.country || '',
      pincode: match.pincode || '',
    });
    setResults([]);
    setSearched(false);
  };

  // Ticks only for a point that will actually save — a ✓ beside "abc" would
  // promise something the database is about to refuse.
  const hasPoint = isValidPoint(point);

  return (
    <div>
      <div className="orgf-searchwrap">
        <OrgField label={label} required={required} error={error} help={help}>
          {({ id, invalid, describedBy }) => (
            (multiline ? (
              <textarea
                id={id}
                className="orgf-area orgf-area--address"
                rows={2}
                value={address ?? ''}
                autoComplete="off"
                placeholder="Search for a place"
                aria-invalid={invalid}
                aria-describedby={describedBy}
                onChange={(event) => onAddressChange(event.target.value)}
              />
            ) : (
              <input
                id={id}
                className="orgf-input"
                type="text"
                value={address ?? ''}
                autoComplete="off"
                placeholder="Search for a place"
                aria-invalid={invalid}
                aria-describedby={describedBy}
                onChange={(event) => onAddressChange(event.target.value)}
              />
            ))
          )}
        </OrgField>

        {results.length > 0 && (
          <ul className="orgf-results">
            {results.map((match) => (
              <li key={`${match.lat},${match.lng},${match.label}`}>
                <button type="button" onClick={() => choose(match)}>
                  <span className="orgf-pin" aria-hidden="true">◉</span>
                  <span>
                    {match.label}
                    <small>{match.lat}, {match.lng}</small>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {searching && <span className="orgf-help">Searching…</span>}
      {!searching && searched && results.length === 0 && (
        <span className="orgf-help">
          {showCoordinates
            ? 'No matches — enter the coordinates below.'
            : 'No matches. Type the place yourself and fill in the fields below.'}
        </span>
      )}

      {showCoordinates && hasPoint && (
        <div className="orgf-tags">
          <span className="orgf-tag">
            <span className="orgf-tick" aria-hidden="true">✓</span> lat <b>{point.lat}</b>
          </span>
          <span className="orgf-tag">
            <span className="orgf-tick" aria-hidden="true">✓</span> lng <b>{point.lng}</b>
          </span>
        </div>
      )}

      {/*
        Gated on `showMap` ALONE, not on `showCoordinates` as well.
        
        The event's City field is the case that distinction exists for: it has
        no geo_point column, so it shows no coordinate boxes — but a map is
        still the fastest way to confirm you picked the right Bengaluru. The pin
        moves, the address follows, and the coordinates are simply not saved.
      */}
      {showMap && (
        <MapPointPicker
          point={point}
          label={label}
          readOnly={readOnly}
          onChange={(next) => {
            // The pin is authoritative about WHERE. It only speaks for the
            // address when the reverse lookup actually returned one, so a
            // silent failure moves the pin and leaves the text alone.
            typedRef.current = false;
            onChange({
              address: next.address ?? address ?? '',
              lat: next.lat,
              lng: next.lng,
              ...(next.address ? {
                state: next.state || '',
                country: next.country || '',
                pincode: next.pincode || '',
              } : {}),
            });
            setResults([]);
            setSearched(false);
          }}
        />
      )}

      {showCoordinates && (
        <button
          type="button"
          className="orgf-link"
          aria-expanded={manualOpen}
          onClick={() => setManualOpen((open) => !open)}
        >
          {manualOpen ? 'Hide manual coordinates' : 'Set coordinates precisely →'}
        </button>
      )}

      {showCoordinates && manualOpen && (
        <div className="orgf-manual">
          <div className="orgf-grid orgf-g2">
            <OrgInput
              label={latLabel} type="text" inputMode="decimal"
              placeholder="e.g. 12.9716"
              value={point?.lat ?? ''}
              error={coordinateError('lat', point?.lat)}
              onChange={(value) => emit({ lat: sanitizeCoordinate(value) })}
            />
            <OrgInput
              label={lngLabel} type="text" inputMode="decimal"
              placeholder="e.g. 77.5946"
              value={point?.lng ?? ''}
              error={coordinateError('lng', point?.lng)}
              onChange={(value) => emit({ lng: sanitizeCoordinate(value) })}
            />
          </div>
        </div>
      )}
    </div>
  );
}
